// Worker pool for parallel HTJ2K slice decoding. Spawns N `decodeWorker`
// instances (each with its own WASM decoder) and keeps N slices in flight at
// once, so the volume decodes across all cores instead of sequentially on the
// main thread.

export interface DecodedSliceResult {
    z: number;
    width: number;
    height: number;
    pixels: Int16Array;
}

interface WorkerReply {
    z: number;
    width?: number;
    height?: number;
    pixels?: Int16Array;
    error?: string;
}

const DEFAULT_POOL_SIZE = Math.min(
    typeof navigator !== 'undefined' ? navigator.hardwareConcurrency || 4 : 4,
    8
);

export interface DecodeAllOptions {
    poolSize?: number;
    /** Aborts the run: terminates the worker pool and rejects with an AbortError. */
    signal?: AbortSignal;
}

/**
 * Decode every slice in `index` (each `[offset, length]` into `buf`) across a
 * worker pool. `onResult` is called once per slice as it finishes (order is not
 * guaranteed — use `r.z`). Resolves when all slices are done; rejects on the
 * first decode error, a throwing `onResult`, or an aborted signal. Workers are
 * always terminated before returning.
 */
export function decodeAll(
    buf: Uint8Array,
    index: [number, number][],
    onResult: (r: DecodedSliceResult) => void,
    { poolSize = DEFAULT_POOL_SIZE, signal }: DecodeAllOptions = {}
): Promise<void> {
    const total = index.length;

    return new Promise<void>((resolve, reject) => {
        if (signal?.aborted) {
            reject(new DOMException('decode aborted', 'AbortError'));
            return;
        }
        // Empty index: nothing to decode. Resolve rather than spawning a worker that
        // would never reach `done === total` and hang the load forever.
        if (total === 0) {
            resolve();
            return;
        }

        const size = Math.max(1, Math.min(poolSize, total));
        const workers: Worker[] = [];
        for (let i = 0; i < size; i++) {
            workers.push(
                new Worker(new URL('./decodeWorker.ts', import.meta.url), { type: 'module' })
            );
        }

        let next = 0;
        let done = 0;
        let settled = false;

        const settle = () => {
            settled = true;
            signal?.removeEventListener('abort', onAbort);
            workers.forEach((w) => w.terminate());
        };
        const fail = (err: Error) => {
            if (settled) return;
            settle();
            reject(err);
        };
        const succeed = () => {
            if (settled) return;
            settle();
            resolve();
        };
        function onAbort() {
            fail(new DOMException('decode aborted', 'AbortError'));
        }
        signal?.addEventListener('abort', onAbort, { once: true });

        const dispatch = (w: Worker) => {
            if (next >= total) return;
            const z = next++;
            const [off, len] = index[z];
            const bytes = buf.slice(off, off + len).buffer as ArrayBuffer;
            w.postMessage({ z, bytes }, [bytes]);
        };

        for (const w of workers) {
            w.onmessage = (e: MessageEvent<WorkerReply>) => {
                if (settled) return;
                const msg = e.data;
                if (msg.error) {
                    fail(new Error(`slice ${msg.z}: ${msg.error}`));
                    return;
                }
                try {
                    onResult({
                        z: msg.z,
                        width: msg.width!,
                        height: msg.height!,
                        pixels: msg.pixels!
                    });
                } catch (err) {
                    fail(err instanceof Error ? err : new Error(String(err)));
                    return;
                }
                done++;
                if (done === total) {
                    succeed();
                    return;
                }
                dispatch(w);
            };
            w.onerror = (ev) => fail(new Error(ev.message || 'decode worker error'));
        }

        for (const w of workers) dispatch(w);
    });
}
